/**
 * Удержания кампании: почему реклама стоит и что будет, если снять.
 *
 * ГЛАВНЫЙ ВОПРОС ЭКРАНА — НЕ «ЧТО ДЕРЖИТ», А «НАЧНУТСЯ ЛИ ПОКАЗЫ».
 * Удержаний бывает несколько сразу: дневной лимит, расписание, прогноз
 * расхода. Снять одно и увидеть, что кампания по-прежнему стоит, —
 * обычное дело, и человек в этот момент решает, что сервис сломан.
 *
 * Поэтому здесь считается не только список, но и остаток: сколько
 * удержаний останется после снятия конкретного. Число это показывается
 * ДО нажатия, в подсказке кнопки.
 */

export interface Hold {
  id: string;
  campaignId: string;
  advertId: number;
  campaignName: string | null;
  /** budget_day | budget_total | forecast | schedule | schedule_pause | … */
  kind: string;
  /** Меньше — важнее. По нему удержания и упорядочены. */
  rank: number;
  reason: string;
  detail: string | null;
  /** Как снимется само: `auto` — движком, `manual` — только руками. */
  releaseKind: string | null;
  createdAt: string;
}

/**
 * Человеческое имя вида удержания.
 *
 * Причина (`reason`) приходит с сервера и уже написана по-русски —
 * здесь только заголовок, чтобы взгляд цеплялся за вид, а не читал
 * фразу целиком. Неизвестный вид не прячется: показывается как есть,
 * иначе новое удержание станет невидимым при первом же появлении.
 */
const TITLES: Record<string, string> = {
  paused_by_human: 'Остановлено вручную',
  budget_day: 'Дневной лимит',
  budget_total: 'Общий лимит',
  forecast: 'Прогноз расхода',
  schedule: 'Расписание',
  schedule_pause: 'Расписание: пауза',
  schedule_start: 'Расписание: запуск',
  resume: 'Возобновление',
};

export function holdTitle(kind: string): string {
  return TITLES[kind] ?? kind;
}

/**
 * Снимется ли удержание само.
 *
 * Разница важная и не видная из названия: дневной лимит отпустит сам
 * в полночь, а общий — не отпустит никогда, пока человек не поднимет
 * порог. Кнопка «снять» у первого означает «не ждать до полуночи»,
 * у второго — «снять насовсем».
 */
export function releasesItself(hold: Hold): boolean {
  return hold.releaseKind === 'auto';
}

/**
 * Что будет, если снять именно это удержание.
 *
 * ЧЕСТНЫЙ ОТВЕТ ЗДЕСЬ ВАЖНЕЕ КРАТКОГО. «Снято» под кампанией, которая
 * продолжает стоять, — это не успех, а недоразумение: человек ждёт
 * показов, их нет, и связать одно с другим ему нечем.
 */
export function afterRelease(all: Hold[], id: string): { rest: number; text: string } {
  const rest = all.filter((h) => h.id !== id).length;
  if (rest === 0) {
    return { rest: 0, text: 'Снять. Больше кампанию ничего не держит — показы начнутся' };
  }
  return {
    rest,
    text:
      rest === 1
        ? 'Снять. Кампанию держит ещё одно удержание — показы не начнутся, пока оно есть'
        : `Снять. Кампанию держат ещё ${rest} — показы не начнутся, пока они есть`,
  };
}

/** Удержания одной кампании, в порядке важности. */
export function forCampaign(all: Hold[], campaignId: string): Hold[] {
  return all
    .filter((h) => h.campaignId === campaignId)
    .sort((a, b) => a.rank - b.rank || a.createdAt.localeCompare(b.createdAt));
}
